import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  SALES_INSIGHTS_DIM_PREFIX,
  SALES_INSIGHTS_OTHERS_KEY,
  SalesInsightsActivityType,
  SalesInsightsActivityTypeRequest,
  SalesInsightsBreakdownResponse,
  SalesInsightsCustomerHealthResponse,
  SalesInsightsCustomerHealthRow,
  SalesInsightsDashboardDefinition,
  SalesInsightsDashboardSummary,
  SalesInsightsExportRequest,
  SalesInsightsFilterOption,
  SalesInsightsFilterValues,
  SalesInsightsGoal,
  SalesInsightsGoalCheck,
  SalesInsightsGoalCustomer,
  SalesInsightsGoalCustomersResponse,
  SalesInsightsGoalInput,
  SalesInsightsGoalOptions,
  SalesInsightsGoalPreview,
  SalesInsightsGoalScope,
  SalesInsightsSourceDefinition,
  SalesInsightsStatus
} from '../models/sales-insights.model';

const PERIOD_ENDPOINTS = ['kpi', 'breakdown'];
const ABSOLUTE_ENDPOINT_PREFIX = 'json/';
const DIMENSION_PLACEHOLDER = 'dim.<name>';
const FILTER_OPTIONS_LIMIT = 200;
const CUSTOMER_SEARCH_LIMIT = 20;

@Injectable({
  providedIn: 'root'
})
export class SalesInsightsService {
  protected http = inject(HttpClient);
  protected baseUrl = 'json/sales-insights';

  getDashboards(): Observable<SalesInsightsDashboardSummary[]> {
    return this.http.get<SalesInsightsDashboardSummary[]>(`${this.baseUrl}/dashboards`);
  }

  getDashboard(id: string): Observable<SalesInsightsDashboardDefinition> {
    return this.http.get<SalesInsightsDashboardDefinition>(`${this.baseUrl}/dashboards/${encodeURIComponent(id)}`);
  }

  getData<T>(
    source: SalesInsightsSourceDefinition,
    filters: SalesInsightsFilterValues,
    accepts?: string[] | null
  ): Observable<T> {
    const includePeriod = accepts ? accepts.includes('period') : PERIOD_ENDPOINTS.includes(source.endpoint);
    return this.http.get<T>(this.endpointUrl(source.endpoint), {
      params: this.buildParams(source.params || {}, filters, includePeriod, accepts)
    });
  }

  endpointUrl(endpoint: string): string {
    return endpoint.startsWith(ABSOLUTE_ENDPOINT_PREFIX) ? endpoint : `${this.baseUrl}/${endpoint}`;
  }

  exportDashboard(id: string, request: SalesInsightsExportRequest): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.baseUrl}/dashboards/${encodeURIComponent(id)}/export`, request, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  getBudgetReport(year: number, salesperson?: string | null): Observable<HttpResponse<Blob>> {
    let params = new HttpParams().set('year', year);
    if (salesperson) {
      params = params.set('salesperson', salesperson);
    }
    return this.http.get(`${this.baseUrl}/budget-report`, { params, observe: 'response', responseType: 'blob' });
  }

  // The active dashboard filters as the request params the backend export applies to every component.
  exportFilters(filters: SalesInsightsFilterValues): { [param: string]: string[] } {
    const result: { [param: string]: string[] } = {};
    const params = this.buildParams({}, filters, true);
    params.keys().forEach(key => (result[key] = params.getAll(key) || []));
    return result;
  }

  getStatus(): Observable<SalesInsightsStatus> {
    return this.http.get<SalesInsightsStatus>(`${this.baseUrl}/status`);
  }

  refresh(): Observable<SalesInsightsStatus> {
    return this.http.post<SalesInsightsStatus>(`${this.baseUrl}/refresh`, {});
  }

  createGoal(goal: SalesInsightsGoalInput): Observable<SalesInsightsGoal> {
    return this.http.post<SalesInsightsGoal>(`${this.baseUrl}/goals`, goal);
  }

  updateGoal(id: string, goal: SalesInsightsGoalInput): Observable<SalesInsightsGoal> {
    return this.http.put<SalesInsightsGoal>(`${this.baseUrl}/goals/${encodeURIComponent(id)}`, goal);
  }

  deleteGoal(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/goals/${encodeURIComponent(id)}`);
  }

  getGoalCustomers(id: string, filters: SalesInsightsFilterValues): Observable<SalesInsightsGoalCustomer[]> {
    return this.getData<SalesInsightsGoalCustomer[] | SalesInsightsGoalCustomersResponse>(
      { endpoint: `goals/${encodeURIComponent(id)}/customers` },
      filters
    ).pipe(map(response => (Array.isArray(response) ? response : response?.rows || [])));
  }

  previewGoal(scope: SalesInsightsGoalScope): Observable<SalesInsightsGoalPreview> {
    return this.http.post<SalesInsightsGoalPreview>(`${this.baseUrl}/goals/preview`, scope);
  }

  searchCustomers(search: string, limit = CUSTOMER_SEARCH_LIMIT): Observable<SalesInsightsCustomerHealthRow[]> {
    const params = new HttpParams().set('search', search).set('limit', limit).set('sort', 'name');
    return this.http
      .get<SalesInsightsCustomerHealthResponse>(`${this.baseUrl}/customer-health`, { params })
      .pipe(map(response => response?.rows || []));
  }

  getCustomersById(ids: string[]): Observable<SalesInsightsCustomerHealthRow[]> {
    let params = new HttpParams().set('sort', 'name');
    ids.forEach(id => (params = params.append('customerId', id)));
    return this.http
      .get<SalesInsightsCustomerHealthResponse>(`${this.baseUrl}/customer-health`, { params })
      .pipe(map(response => response?.rows || []));
  }

  getGoalOptions(): Observable<SalesInsightsGoalOptions> {
    return this.http.get<SalesInsightsGoalOptions>(`${this.baseUrl}/goals/options`);
  }

  getActiveGoals(): Observable<SalesInsightsGoal[]> {
    return this.http
      .get<SalesInsightsGoal[]>(`${this.baseUrl}/goals`)
      .pipe(map(goals => (goals || []).filter(goal => !goal.archived && !goal.expired)));
  }

  getActivityTypes(): Observable<SalesInsightsActivityType[]> {
    return this.http.get<SalesInsightsActivityType[]>(`${this.baseUrl}/activity-types`);
  }

  createActivityType(request: SalesInsightsActivityTypeRequest): Observable<SalesInsightsActivityType> {
    return this.http.post<SalesInsightsActivityType>(`${this.baseUrl}/activity-types`, request);
  }

  updateActivityType(code: string, request: SalesInsightsActivityTypeRequest): Observable<SalesInsightsActivityType> {
    return this.http.put<SalesInsightsActivityType>(`${this.baseUrl}/activity-types/${encodeURIComponent(code)}`, request);
  }

  checkGoalCustomer(id: string, check: SalesInsightsGoalCheck): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/goals/${encodeURIComponent(id)}/check`, check);
  }

  // Backed by breakdown until the backend has a dedicated filters endpoint.
  getFilterOptions(filter: string): Observable<SalesInsightsFilterOption[]> {
    const params = new HttpParams()
      .set('source', 'invoice')
      .set('period', 'r12')
      .set('groupBy', filter)
      .set('limit', FILTER_OPTIONS_LIMIT);
    return this.http.get<SalesInsightsBreakdownResponse>(`${this.baseUrl}/breakdown`, { params }).pipe(
      map(response =>
        (response?.rows || [])
          .filter(row => row.key && row.key !== SALES_INSIGHTS_OTHERS_KEY)
          .map(row => ({ id: row.key, name: row.name || row.key }))
          .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
      )
    );
  }

  buildParams(
    sourceParams: { [key: string]: string | number | boolean },
    filters: SalesInsightsFilterValues,
    includePeriod = true,
    accepts?: string[] | null
  ): HttpParams {
    const accepted = (param: string) => !accepts || accepts.includes(param);
    let params = new HttpParams();
    Object.entries(sourceParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value);
      }
    });

    if (includePeriod && filters.period) {
      params = params.set('period', filters.period).delete('from').delete('to');
    }
    if (accepted('customerId')) {
      (filters.customerId || []).filter(id => !!id).forEach(id => (params = params.append('customerId', id)));
    }
    if (filters.salesperson && accepted('salesperson')) {
      params = params.set('salesperson', filters.salesperson);
    }
    if (filters.country && accepted('country')) {
      params = params.set('country', filters.country);
    }
    Object.entries(filters.dims || {}).forEach(([name, value]) => {
      const param = `${SALES_INSIGHTS_DIM_PREFIX}${name}`;
      if (value && (accepted(param) || accepted(DIMENSION_PLACEHOLDER))) {
        params = params.set(param, value);
      }
    });
    return params;
  }

  isDimensionFilter(filter: string): boolean {
    return filter.startsWith(SALES_INSIGHTS_DIM_PREFIX);
  }

  dimensionName(filter: string): string {
    return filter.substring(SALES_INSIGHTS_DIM_PREFIX.length);
  }
}
